import { parseWithZod } from '@conform-to/zod';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useState } from 'react';
import { useFetcher, data } from 'react-router';
import { useAuthenticityToken } from 'remix-utils/csrf/react';
import { z } from 'zod';

import { getHints } from '~/components/client-hints';
import { StatusButton } from '~/components/ui/status-button';
import {
  createDropInPassPayment,
  type CreateDropInPassPaymentResponse,
} from '~/domain/drop-in-passes/handlers/create-drop-in-pass-payment.server';
import { type UserDropInPass } from '~/domain/user-drop-in-passes/models/user-drop-in-pass';
import { requireSession } from '~/utils/auth.server';
import { validateCSRF } from '~/utils/csrf.server';
import { fromStripeFormat } from '~/utils/currency';
import { handleDomainError } from '~/utils/errors.server';
import { useFetcherWithPromise } from '~/utils/fetcher-with-promise';
import { getGymIdAndSlugFromParams } from '~/utils/gyms.server';

import {
  DefaultPromoCodeHelperContent,
  type PromoCode,
  PurchaseModal,
} from '../components/purchase-modal';
import { PurchaseSelect } from '../components/purchase-select';
import { PurchaseSummary } from '../components/purchase-summary';

import { type Route } from './+types/purchase-drop-in';
import { type PromoCodeValidationResponse } from './validate-promo';

const schema = z.object({
  dropInPassId: z.coerce.number(),
  promoCode: z.string().optional(),
});

export async function action({ request, params }: Route.ActionArgs) {
  const session = await requireSession(request);
  const { gymId } = await getGymIdAndSlugFromParams(params);
  const { timeZone } = getHints(request);

  const formData = await request.formData();
  await validateCSRF(formData, request.headers);

  const submission = parseWithZod(formData, { schema });
  if (submission.status !== 'success') {
    return submission.reply();
  }

  const { dropInPassId, promoCode } = submission.value;

  const res = await createDropInPassPayment(session, timeZone, gymId, dropInPassId, promoCode);
  if (res.isErr()) {
    return handleDomainError(res);
  }

  return data(res.value);
}

interface GymDropInPass {
  id: number;
  numberOfDays: number;
  passPriceCents: number;
  serviceFeeCents: number;
  finalPriceCents: number;
}

interface PurchaseDropInButtonProps {
  gymSlugOrId: string;
  gymDropInPasses: GymDropInPass[];
  timeZone: string;
  userDropInPass: UserDropInPass | null;
}

export function PurchaseDropInButton({
  gymDropInPasses,
  gymSlugOrId,
  userDropInPass,
  timeZone,
}: PurchaseDropInButtonProps) {
  const [showingModal, setShowingModal] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState<PromoCode | undefined>();
  const [amount, setAmount] = useState<number | undefined>();
  const [amountBelowMinimum, setAmountBelowMinimum] = useState(false);
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const [paymentId, setPaymentId] = useState<string | undefined>();

  const fetcher = useFetcher<CreateDropInPassPaymentResponse>();
  const validatePromoFetcher = useFetcherWithPromise<PromoCodeValidationResponse>();
  const csrf = useAuthenticityToken();
  const [selectedDropInPass, setSelectedDropInPass] = useState<GymDropInPass | null>(null);

  const formattedValidToDate = userDropInPass
    ? formatInTimeZone(userDropInPass.validTo, timeZone, 'MM/dd/yyyy')
    : '';
  const formattedValidFromDate = userDropInPass
    ? formatInTimeZone(userDropInPass.validFrom, timeZone, 'MM/dd/yyyy')
    : '';

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      setStripeClientSecret(fetcher.data.clientSecret);
      setPromoCode(fetcher.data.promoCode);
      setAmount(fetcher.data.amount);
      setAmountBelowMinimum(fetcher.data.isBelowMinimum);
      setPromoCodeInvalid(fetcher.data.promoCodeInvalid);
      setPaymentId(fetcher.data.paymentId);
      setShowingModal(true);
    }
  }, [fetcher, setShowingModal, setStripeClientSecret]);

  const handleShowModal = () => {
    const body = new FormData();
    body.append('csrf', csrf);
    body.append('dropInPassId', (selectedDropInPass?.id ?? 0).toString());

    fetcher.submit(body, {
      method: 'POST',
      action: `/gym/${gymSlugOrId}/resources/purchase-drop-in`,
    });
  };

  const handleModalClose = () => {
    setPromoCode(undefined);
    setAmount(undefined);
    setAmountBelowMinimum(false);
    setPromoCodeInvalid(false);
    setShowingModal(false);
    setPaymentId(undefined);
  };

  const sortedGymDropInPasses = [...gymDropInPasses].sort((a, b) => a.numberOfDays - b.numberOfDays);
  const handleSelectChange = (dropInPassId: string) => {
    const selectedPass = gymDropInPasses.find((pass) => pass.id.toString() === dropInPassId);
    setSelectedDropInPass(selectedPass || null);
  };

  const handlePromoCodeSubmit = (promoCode: string) => {
    fetcher.submit(
      {
        csrf,
        dropInPassId: (selectedDropInPass?.id ?? 0).toString(),
        promoCode,
      },
      {
        method: 'POST',
        action: `/gym/${gymSlugOrId}/resources/purchase-drop-in`,
      },
    );
  };

  const handlePaymentPreCheck = useCallback(async (): Promise<string | null> => {
    if (!promoCode) {
      return null;
    }

    const data = await validatePromoFetcher.submit(
      { csrf, promoCodeName: promoCode.name },
      {
        method: 'POST',
        action: `/gym/${gymSlugOrId}/resources/validate-promo`,
      },
    );

    if (!data?.valid) {
      return 'This promo code is no longer valid.';
    }

    return null;
  }, [csrf, promoCode, validatePromoFetcher, gymSlugOrId]);

  const isSubmitting = fetcher.state !== 'idle';

  return (
    <>
      <div className="flex flex-col gap-4">
        {userDropInPass === null ? (
          <>
            <div className="mt-2 flex flex-col gap-4 text-body2">
              <p>
                Not a member but still want to train? We’ve got you covered. Buy a Drop-In Pass
                below.
              </p>
              <p>Drop-In Passes activate immediately upon purchase.</p>
            </div>

            <div className="flex items-center gap-4">
              <PurchaseSelect
                items={sortedGymDropInPasses.map((pass) => ({
                  id: pass.id.toString(),
                  name: `${pass.numberOfDays} Day Drop-In Pass`,
                  priceCents: pass.finalPriceCents,
                }))}
                onChange={handleSelectChange}
                placeholder="Select a pass or ticket…"
              />
              <StatusButton
                className="h-[30px] min-w-[77px]"
                onClick={handleShowModal}
                disabled={selectedDropInPass === null || isSubmitting}
                type="submit"
                color="primary"
                status={isSubmitting ? 'pending' : 'idle'}
              >
                Buy
              </StatusButton>
            </div>
          </>
        ) : (
          <PurchaseSummary
            purchaseName={`${userDropInPass.numberOfDays} Day Drop-In Pass`}
            purchaseItems={[
              { name: 'Purchased', value: formattedValidFromDate },
              { name: 'Price', value: fromStripeFormat(userDropInPass.amount) },
              {
                name: 'Valid Through',
                value:
                  formattedValidToDate !== formattedValidFromDate
                    ? `${formattedValidFromDate} - ${formattedValidToDate}`
                    : formattedValidToDate,
              },
            ]}
          />
        )}
      </div>

      {showingModal && (stripeClientSecret !== null || paymentId !== undefined) ? (
        <PurchaseModal
          open
          onClose={handleModalClose}
          stripeClientSecret={stripeClientSecret}
          returnUrl={`${window.location.protocol}//${window.location.host}/gym/${gymSlugOrId}/resources/purchase-drop-in-return`}
          title="Purchase Pass"
          onPromoCodeSubmit={handlePromoCodeSubmit}
          promoCode={promoCode}
          promoCodeInvalid={promoCodeInvalid}
          isLoading={isSubmitting}
          onPrePaymentCheck={handlePaymentPreCheck}
          paymentId={paymentId}
          helperContent={
            <DefaultPromoCodeHelperContent amount={amount} amountBelowMinimum={amountBelowMinimum} />
          }
          content={
            <div>
              <p className="text-subtitle2">{`${selectedDropInPass?.numberOfDays ?? 0} Day Drop-In Pass`}</p>
              <p className="text-h4 font-normal">
                {fromStripeFormat(amount ?? selectedDropInPass?.finalPriceCents ?? 0)}
              </p>
              <p className="mb-6 text-subtitle2">
                {fetcher.data?.gymName ?? ''} – valid through {fetcher.data?.date ?? ''}
              </p>
            </div>
          }
        />
      ) : null}
    </>
  );
}
